import { getAccounts, deleteAccount as deleteAccountApi } from '../services/api';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createEntityStore } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import i18n from '../i18n';

interface Account {
  id: string;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'INCOME' | 'EXPENSE';
  description?: string;
  isActive?: boolean;
  balance?: number;
  createdByName?: string;
  createdByRole?: string;
  createdByUserId?: string;
  _count?: {
    journalDetails: number;
  };
}

const useAccountStoreBase = createEntityStore<Account>({
  entityName: 'account',
  fetchFn: (page: number, isActive: boolean = true) => getAccounts(page, 20, isActive),
  deleteFn: deleteAccountApi,
  toast,
  messages: {
    deleteSuccess: () => i18n.t('common.messages.deleteSuccess'),
    deleteError: () => i18n.t('common.messages.deleteFailed'),
    fetchError: () => i18n.t('common.messages.fetchFailed'),
  },
});

// 封存會計科目 store
const useArchivedAccountStoreBase = createEntityStore<Account>({
  entityName: 'account',
  fetchFn: (page: number) => getAccounts(page, 20, false),
  deleteFn: deleteAccountApi,
  toast,
  messages: {
    deleteSuccess: () => i18n.t('common.messages.deleteSuccess'),
    deleteError: () => i18n.t('common.messages.deleteFailed'),
    fetchError: () => i18n.t('common.messages.fetchFailed'),
  },
});

export const useArchivedAccountStore = () => {
  const store = useArchivedAccountStoreBase();
  
  return {
    accounts: store.items,
    pagination: store.pagination,
    currentPage: store.currentPage,
    error: store.error,
    isLoading: store.isLoading,
    isPageLoading: store.isPageLoading,
    showDeleteConfirmModal: store.showDeleteConfirmModal,
    itemToDeleteId: store.itemToDeleteId,
    isDeleting: store.isDeleting,
    fetchAccounts: store.fetchItems,
    setCurrentPage: store.setCurrentPage,
    handleDelete: store.handleDelete,
    confirmDelete: store.confirmDelete,
    closeDeleteModal: store.closeDeleteModal,
    refreshData: store.refreshData,
    updateAccount: store.updateItem,
    addAccount: store.addItem,
    getAccountById: store.getItemById,
    clearAllAccounts: () => {}, // 封存頁面不需要清除科目選擇器，提供空實現
  };
};

// 會計科目選擇器專用快取 store
interface AccountSelectorState {
  allAccounts: Account[];
  isLoadingAll: boolean;
  lastAllAccountsFetch: number | null;
  
  fetchAllAccounts: (force?: boolean) => Promise<void>;
  clearAllAccounts: () => void;
}


const useAccountSelectorStore = create<AccountSelectorState>()(
  persist(
    (set, get) => ({
      allAccounts: [],
      isLoadingAll: false,
      lastAllAccountsFetch: null,
      
      fetchAllAccounts: async (force = false) => {
        const { lastAllAccountsFetch, isLoadingAll } = get();
        const now = Date.now();
        
        // 如果正在載入或快取還有效且不強制重新載入，直接返回
        if (isLoadingAll || (!force && lastAllAccountsFetch)) {
          return;
        }
        
        set({ isLoadingAll: true });
        
        try {
          // 獲取所有會計科目（不分頁）
          const response = await getAccounts(1, 1000); // 假設不會超過 1000 個科目
          const accountsData = response.data?.data || response.data || [];
          
          set({
            allAccounts: accountsData,
            lastAllAccountsFetch: now,
            isLoadingAll: false,
          });
        } catch (error) {
          console.error('載入會計科目下拉選單失敗:', error);
          set({
            allAccounts: [],
            isLoadingAll: false,
          });
        }
      },
      
      clearAllAccounts: () => {
        set({
          allAccounts: [],
          lastAllAccountsFetch: null,
        });
      },
    }),
    {
      name: 'account-selector-store',
      partialize: (state) => ({
        allAccounts: state.allAccounts,
        lastAllAccountsFetch: state.lastAllAccountsFetch,
      }),
    }
  )
);

// 導出基礎 store 供其他模組使用
export { useAccountStoreBase, useArchivedAccountStoreBase };

export const useAccountStore = () => {
  const store = useAccountStoreBase();
  const selectorStore = useAccountSelectorStore();
  
  // 覆蓋會計科目刪除邏輯
  const confirmDeleteWithCascade = async () => {
    const state = store;
    const itemToDeleteId = state.itemToDeleteId;
    if (!itemToDeleteId) return;

    // 立即保存原始狀態，用於錯誤時復原
    const originalItems = [...state.items];
    const originalPagination = state.pagination ? { ...state.pagination } : null;

    // 同時關閉視窗並移除項目
    const newItems = state.items.filter(item => item.id !== itemToDeleteId);
    const newPagination = state.pagination ? {
      ...state.pagination,
      totalItems: Math.max(0, state.pagination.totalItems - 1)
    } : null;

    // 更新會計科目列表
    useAccountStoreBase.setState({
      showDeleteConfirmModal: false,
      itemToDeleteId: null,
      isDeleting: false,
      items: newItems,
      pagination: newPagination,
      lastOptimisticUpdate: Date.now(),
    });

    try {
      // 執行刪除 API
      await deleteAccountApi(itemToDeleteId);

      // 顯示成功訊息
      toast.success(i18n.t('common.messages.deleteSuccess'));

      // 立即更新選擇器快取，移除被刪除的科目
      const currentAllAccounts = selectorStore.allAccounts;
      const updatedAllAccounts = currentAllAccounts.filter(account => account.id !== itemToDeleteId);
      useAccountSelectorStore.setState({
        allAccounts: updatedAllAccounts,
      });

      // 清除所有年份的報表快取（因為科目總數變更）
      import('./reportStore').then(({ useReportStore }) => {
        const reportStore = useReportStore.getState();
        reportStore.clearAllCache();
      });

    } catch (err: any) {
      // 失敗時恢復原始狀態
      useAccountStoreBase.setState({
        items: originalItems,
        pagination: originalPagination,
        error: i18n.t('common.messages.deleteFailed')
      });

      // 顯示錯誤訊息
      toast.error(i18n.t('common.messages.deleteFailed'));
    }
  };
  
  return {
    accounts: store.items,
    pagination: store.pagination,
    currentPage: store.currentPage,
    error: store.error,
    isLoading: store.isLoading,
    isPageLoading: store.isPageLoading,
    showDeleteConfirmModal: store.showDeleteConfirmModal,
    itemToDeleteId: store.itemToDeleteId,
    isDeleting: store.isDeleting,
    fetchAccounts: store.fetchItems,
    setCurrentPage: store.setCurrentPage,
    handleDelete: store.handleDelete,
    confirmDelete: confirmDeleteWithCascade, // 使用自定義的級聯刪除
    closeDeleteModal: store.closeDeleteModal,
    refreshData: store.refreshData,
    updateAccount: (id: string, account: Partial<Account>) => {
      // 更新主列表
      store.updateItem(id, account);
      
      // 同步更新選擇器快取
      const currentAllAccounts = selectorStore.allAccounts;
      const updatedAllAccounts = currentAllAccounts.map(a => 
        a.id === id ? { ...a, ...account } : a
      );
      useAccountSelectorStore.setState({
        allAccounts: updatedAllAccounts,
      });
    },
    addAccount: (account: Account) => {
      // 添加到主列表
      store.addItem(account);
      
      // 同步更新選擇器快取
      const currentAllAccounts = selectorStore.allAccounts;
      const updatedAllAccounts = [...currentAllAccounts, account];
      useAccountSelectorStore.setState({
        allAccounts: updatedAllAccounts,
      });
    },
    getAccountById: store.getItemById,
    // 選擇器專用
    allAccounts: selectorStore.allAccounts,
    isLoadingAllAccounts: selectorStore.isLoadingAll,
    fetchAllAccounts: selectorStore.fetchAllAccounts,
    clearAllAccounts: selectorStore.clearAllAccounts,
  };
};